import type { InteractionEvent } from '../../interaction/types';
import { COLOR_3D_LEVEL_RANGE } from '../../services/color-3d-presets';
import type { Color3DService } from '../../services/color-3d-service';
import type { WindowLevelService } from '../../services/window-level-service';
import type { IViewport } from '../../viewports/types';
import { is3DViewport } from '../../viewports/viewport-categories';
import { FrameCoalescer } from '../camera/frame-coalescer';
import type { ITool } from '../types';

/**
 * What a drag means on one family of viewports: how far a full-canvas drag
 * reaches, and where the deltas land.
 */
interface ContrastAdapter {
    /** HU spanned by a drag across the canvas's longest edge. */
    readonly dragRange: number;
    /** Apply the already-scaled drag deltas (in HU) to the owning service. */
    apply(dx: number, dy: number): void;
}

/**
 * Contrast adjustment tool — drags to change how the image maps intensities to
 * pixels, on 2D slices, CPR and the 3D volume alike.
 *
 * On slices and CPR the drag is a plain window/level on the image property. On
 * the volume it applies a window/level to the transfer function: the level
 * slides the whole colormap along the intensity axis (low level reveals soft
 * tissue, high level keeps only bone and contrast agent), the window stretches
 * it — it changes no colors.
 *
 * The axes are the transpose of each other on purpose, matching the desktop
 * viewer's two windowing modes:
 *
 *   2D  horizontal → window (width)   vertical → level (center)
 *   3D  horizontal → level (center)   vertical → window (width)
 *
 * The tool never renders. Every viewport that responds to contrast re-renders
 * itself from its own WINDOW_LEVEL_CHANGED subscription (see SliceViewport2D,
 * CprViewport2D, VolumeViewport), so a render here would be a second render of
 * the dragged viewport on every drag frame.
 */
export class ContrastTool implements ITool {
    static readonly toolName = 'Contrast';

    readonly id = ContrastTool.toolName;
    readonly name = 'Contrast';
    readonly cursor = 'crosshair';
    readonly completed = false;

    private pendingDx = 0;
    private pendingDy = 0;
    private pendingViewport: IViewport | null = null;
    private coalescer = new FrameCoalescer(() => this.applyPending());

    private readonly sliceAdapter: ContrastAdapter;
    private readonly volumeAdapter: ContrastAdapter;

    constructor(windowLevelService: WindowLevelService, color3DService: Color3DService) {
        this.sliceAdapter = {
            dragRange: 2000,
            apply: (dx, dy) => {
                // Inverted dy: drag up = higher level
                windowLevelService.setWindowLevel(
                    windowLevelService.window + dx,
                    windowLevelService.level - dy
                );
            },
        };

        this.volumeAdapter = {
            // A full canvas drag covers the tool's whole level range — the same
            // range Color3DService clamps the result to.
            dragRange: COLOR_3D_LEVEL_RANGE.max - COLOR_3D_LEVEL_RANGE.min,
            apply: (dx, dy) => {
                const { window, level } = color3DService.getWindowLevel();
                // Inverted dy: drag up = wider window
                color3DService.setWindowLevel(window - dy, level + dx);
            },
        };
    }

    activate(_viewport: IViewport): void {}

    deactivate(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    cancel(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    handleEvent(event: InteractionEvent): boolean {
        if (event.type === 'mouse_drag_end') {
            this.coalescer.flush();
            return true;
        }

        if (event.type !== 'mouse_drag') return false;

        const [dx, dy] = event.deltaPoints.canvas;
        this.pendingDx += dx;
        this.pendingDy += dy;
        this.pendingViewport = event.viewport;
        this.coalescer.schedule();
        return true;
    }

    private applyPending(): void {
        const vp = this.pendingViewport;
        const dx = this.pendingDx;
        const dy = this.pendingDy;
        this.resetPending();

        if (!vp || (dx === 0 && dy === 0)) return;

        const canvas = vp.getCanvas();
        if (!canvas) return;

        // Resolved per drag rather than on activate(): a ToolGroup may span
        // viewport types, and the dragged viewport is the one that decides.
        const adapter = is3DViewport(vp.type) ? this.volumeAdapter : this.sliceAdapter;

        const { width, height } = canvas.getBoundingClientRect();
        const scale = adapter.dragRange / Math.max(height, width);

        adapter.apply(dx * scale, dy * scale);
    }

    private resetPending(): void {
        this.pendingDx = 0;
        this.pendingDy = 0;
        this.pendingViewport = null;
    }
}
